import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Sidebar() {
  const { authStatus, switchOrganization, installNewOrganization, logout } = useAuth();
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setOrgMenuOpen(false);
      }
    }

    if (orgMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [orgMenuOpen]);

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <img src="/white-logo.svg" alt="Yukon" className="logo-img" />
        </div>
      </div>

      <nav className="sidebar-nav">
        <NavLink to="/" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} end>
          <i className="nav-icon fa-solid fa-code-branch"></i>
          <span className="nav-text">Repositories</span>
        </NavLink>
        <NavLink
          to="/schedules"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <i className="nav-icon fa-solid fa-calendar"></i>
          <span className="nav-text">Schedules</span>
        </NavLink>
        <NavLink
          to="/integrations"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <i className="nav-icon fa-solid fa-puzzle-piece"></i>
          <span className="nav-text">Integrations</span>
        </NavLink>
        <NavLink
          to="/api-docs"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <i className="nav-icon fa-solid fa-book"></i>
          <span className="nav-text">API Docs</span>
        </NavLink>
        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <i className="nav-icon fa-solid fa-gear"></i>
          <span className="nav-text">Settings</span>
        </NavLink>
      </nav>

      {/* Organization Selector */}
      {authStatus.organizations && authStatus.organizations.length > 0 && (
        <div className="org-selector">
          <label className="org-label">Organization</label>
          <div className="org-dropdown" ref={orgDropdownRef}>
            <button className="org-current" onClick={() => setOrgMenuOpen(!orgMenuOpen)}>
              <span>{authStatus.currentOrganization?.name || 'Select Organization'}</span>
              {authStatus.currentOrganization?.subscriptionTier && (
                <span
                  className={`org-badge ${authStatus.currentOrganization.subscriptionTier === 'PRO' ? 'pro' : 'free'}`}
                >
                  {authStatus.currentOrganization.subscriptionTier}
                </span>
              )}
              <i className="fa-solid fa-chevron-down"></i>
            </button>
            {orgMenuOpen && (
              <div className="org-menu">
                {authStatus.organizations.map(org => (
                  <button
                    key={org.id}
                    className={`org-item ${authStatus.currentOrganization?.id === org.id ? 'active' : ''}`}
                    onClick={() => {
                      switchOrganization(org.id);
                      setOrgMenuOpen(false);
                    }}
                  >
                    <span>{org.name}</span>
                    <span
                      className={`org-badge ${org.subscriptionTier === 'PRO' ? 'pro' : 'free'}`}
                    >
                      {org.subscriptionTier}
                    </span>
                  </button>
                ))}
                <div className="org-divider"></div>
                <button
                  className="org-item org-new"
                  onClick={() => {
                    installNewOrganization();
                    setOrgMenuOpen(false);
                  }}
                >
                  <i className="fa-solid fa-plus"></i>
                  <span>Install on new organization</span>
                </button>
              </div>
            )}
          </div>
          <NavLink to="/billing" className="org-billing-link">
            <i className="fa-solid fa-credit-card"></i>
            <span>Billing & Membership</span>
          </NavLink>
        </div>
      )}

      {/* User Profile Section */}
      {authStatus.user && (
        <div className="user-profile">
          <div className="user-info">
            {authStatus.user.avatar_url && (
              <img className="user-avatar" src={authStatus.user.avatar_url} alt="User avatar" />
            )}
            <div className="user-details">
              <div className="user-name">{authStatus.user.name || authStatus.user.login}</div>
              <div className="user-login">@{authStatus.user.login}</div>
            </div>
          </div>
          <button onClick={logout} className="logout-btn">
            <i className="fa-solid fa-right-from-bracket"></i>
            <span>Logout</span>
          </button>
        </div>
      )}

      <div className="sidebar-footer">
        <div className="connection-status">
          <span className={`status-dot ${authStatus.currentInstallation ? 'active' : ''}`}></span>
          <span>{authStatus.currentInstallation ? 'Connected' : 'Not Connected'}</span>
        </div>
      </div>
    </aside>
  );
}
